import { Menubar, MenubarMenu, MenubarTrigger, MenubarContent, MenubarItem, MenubarSeparator } from "../components/ui/menubar"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { cn } from "../lib/utils"
import { liftV4Props } from "./element-renderer"

export function MenubarRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("menu")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  // JITRULE Task 2: menu/item children may nest their fields under `props`.
  const menus = (element.children ?? []).map(liftV4Props)

  return (
    <Menubar
      className={cn(element.className)}
      data-testid={`menubar-${element.id}`}
      data-atom-type="menu"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      aria-label={element.ariaLabel ?? "Application menubar"}
    >
      {menus.map((menu) => {
        const subItems = (menu.children ?? []).map(liftV4Props)
        const menuLabel = menu.text
          ? String(resolveSymbol(menu.text) ?? "")
          : menu.id

        return (
          <MenubarMenu key={menu.id}>
            <MenubarTrigger
              aria-label={menu.ariaLabel ?? menuLabel}
            >
              {menuLabel}
            </MenubarTrigger>
            {subItems.length > 0 && (
              <MenubarContent>
                {subItems.map((item, i) => {
                  if (item.type === "separator") {
                    return <MenubarSeparator key={item.id ?? `sep-${i}`} />
                  }
                  const label = item.text
                    ? String(resolveSymbol(item.text) ?? "")
                    : item.id
                  return (
                    <MenubarItem
                      key={item.id}
                      onSelect={() => {
                        dispatchEvent(item.id, "tap", { value: label })
                      }}
                      aria-label={item.ariaLabel ?? label}
                    >
                      {label}
                    </MenubarItem>
                  )
                })}
              </MenubarContent>
            )}
          </MenubarMenu>
        )
      })}
    </Menubar>
  )
}
